import { useEffect, useState } from "react";
import useFormDraft from "../../hooks/useFormDraft";
import "./AddWorker.css";
import { userRequest } from "../../RequestMethods";
import { ToastContainer, toast } from 'react-toastify';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button } from "@mui/material";
import {
  UilUser, UilEnvelope, UilLock, UilPhone, UilMapMarker,
  UilGlobe, UilPostcard, UilInstagram, UilShareAlt, UilShield,
} from "@iconscout/react-unicons";
import ProfileImageUploader from "../../Components/ProfileImageUploader/ProfileImageUploader";

export const AddWorker = () => {
  // Not persisted via useFormDraft on purpose — must be re-typed for every
  // submission, so leaving this page open on an unlocked session doesn't
  // let someone else create a worker without knowing the passcode.
  const [creationPasscode, setCreationPasscode] = useState("");
  const [showPasscodeModal, setShowPasscodeModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [userData, setUserData, clearWorkerDraft] = useFormDraft("add-worker", {
    img: null,
    name: "",
    surname: "",
    email: "",
    password: "",
    number: "",
    address: "",
    country: "",
    city: "",
    postalCode: "",
    instagram: "",
    tiktok: "",
    roleId: "",
  });

  const [roles, setRoles] = useState([]);

  useEffect(() => {
    userRequest
      .get("roles")
      .then((res) => setRoles(res.data.roles || res.data))
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserData({
      ...userData,
      [name]: type === "checkbox" ? checked : value
    });
  };

  // The form's own submit just opens the passcode modal — the actual
  // request only fires once the code is confirmed there.
  const handleSubmit = (e) => {
    e.preventDefault();
    setShowPasscodeModal(true);
  };

  const handleCancelPasscode = () => {
    setShowPasscodeModal(false);
    setCreationPasscode("");
  };

  const handleConfirmPasscode = async () => {
    setSubmitting(true);
    try {
      await userRequest.post("workers/register", { ...userData, creationPasscode });

      clearWorkerDraft();
      setCreationPasscode("");
      setShowPasscodeModal(false);
      toast.success("Utilisateur ajouté avec succès !");
    } catch (err) {
      console.error("Erreur lors de l'ajout :", err);
      toast.error(err.response?.data?.message || "Erreur lors de l'ajout de l'utilisateur.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageSelect = (file) => {
    setUserData((prev) => ({
      ...prev,
      img: file,
    }));
  };

  return (
    <div className="add-worker-page">
      <h1 className="add-worker-page-title">Nouvel Employé</h1>

      <div className="add-worker-card">
        <form className="add-worker-form" onSubmit={handleSubmit}>
          <div className="worker-photo-section">
            <ProfileImageUploader onImageSelect={handleImageSelect} />
          </div>

          <div className="worker-section">
            <h4 className="worker-section-title">Informations personnelles</h4>
            <div className="worker-field-grid">
              <div className="worker-field">
                <label>Nom</label>
                <div className="worker-input">
                  <UilUser size={18} className="worker-input-icon" />
                  <input name="name" placeholder="Nom" value={userData.name} onChange={handleChange} required />
                </div>
              </div>
              <div className="worker-field">
                <label>Prénom</label>
                <div className="worker-input">
                  <UilUser size={18} className="worker-input-icon" />
                  <input name="surname" placeholder="Prénom" value={userData.surname} onChange={handleChange} required />
                </div>
              </div>
              <div className="worker-field">
                <label>E-mail</label>
                <div className="worker-input">
                  <UilEnvelope size={18} className="worker-input-icon" />
                  <input type="email" name="email" placeholder="E-mail" value={userData.email} onChange={handleChange} required />
                </div>
              </div>
              <div className="worker-field">
                <label>Mot de passe</label>
                <div className="worker-input">
                  <UilLock size={18} className="worker-input-icon" />
                  <input type="password" name="password" placeholder="Mot de passe" value={userData.password} onChange={handleChange} required />
                </div>
              </div>
              <div className="worker-field">
                <label>Téléphone</label>
                <div className="worker-input">
                  <UilPhone size={18} className="worker-input-icon" />
                  <input name="number" placeholder="Numéro de téléphone" value={userData.number} onChange={handleChange} required />
                </div>
              </div>
              <div className="worker-field">
                <label>Rôle</label>
                <div className="worker-input">
                  <UilShield size={18} className="worker-input-icon" />
                  <select name="roleId" value={userData.roleId} onChange={handleChange}>
                    <option value="">-- Rôle (optionnel, "Staff" par défaut) --</option>
                    {roles.map((role) => (
                      <option key={role._id} value={role._id}>
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="worker-section">
            <h4 className="worker-section-title">Adresse</h4>
            <div className="worker-field-grid">
              <div className="worker-field worker-field-wide">
                <label>Adresse</label>
                <div className="worker-input">
                  <UilMapMarker size={18} className="worker-input-icon" />
                  <input name="address" placeholder="Adresse" value={userData.address} onChange={handleChange} />
                </div>
              </div>
              <div className="worker-field">
                <label>Pays</label>
                <div className="worker-input">
                  <UilGlobe size={18} className="worker-input-icon" />
                  <input name="country" placeholder="Pays" value={userData.country} onChange={handleChange} />
                </div>
              </div>
              <div className="worker-field">
                <label>Ville</label>
                <div className="worker-input">
                  <UilMapMarker size={18} className="worker-input-icon" />
                  <input name="city" placeholder="Ville" value={userData.city} onChange={handleChange} />
                </div>
              </div>
              <div className="worker-field">
                <label>Code postal</label>
                <div className="worker-input">
                  <UilPostcard size={18} className="worker-input-icon" />
                  <input name="postalCode" placeholder="Code Postal" value={userData.postalCode} onChange={handleChange} />
                </div>
              </div>
            </div>
          </div>

          <div className="worker-section">
            <h4 className="worker-section-title">Réseaux sociaux (optionnel)</h4>
            <div className="worker-field-grid">
              <div className="worker-field">
                <label>Instagram</label>
                <div className="worker-input">
                  <UilInstagram size={18} className="worker-input-icon" />
                  <input name="instagram" placeholder="Instagram" value={userData.instagram} onChange={handleChange} />
                </div>
              </div>
              <div className="worker-field">
                <label>TikTok</label>
                <div className="worker-input">
                  <UilShareAlt size={18} className="worker-input-icon" />
                  <input name="tiktok" placeholder="TikTok" value={userData.tiktok} onChange={handleChange} />
                </div>
              </div>
            </div>
          </div>

          <button type="submit" className="worker-submit-btn">Créer l'employé</button>
        </form>
      </div>

      <Dialog open={showPasscodeModal} onClose={handleCancelPasscode}>
        <DialogTitle>Code de sécurité requis</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            type="password"
            label="Code de sécurité admin"
            fullWidth
            value={creationPasscode}
            onChange={(e) => setCreationPasscode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && creationPasscode && !submitting) handleConfirmPasscode();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelPasscode} disabled={submitting}>Annuler</Button>
          <Button
            onClick={handleConfirmPasscode}
            variant="contained"
            disabled={!creationPasscode || submitting}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="top-right" />
    </div>
  );
};
